/**
 * Navigation singleton — allows non-React services to imperatively navigate.
 *
 * Usage:
 *   // In a root React component (once, on mount):
 *   import { setNavigate } from './services/navigation'
 *   setNavigate(navigate)
 *
 *   // In any service / utility:
 *   import { serviceNavigate } from './services/navigation'
 *   serviceNavigate('/staging?siteId=123')
 */

type NavigateOptions = { replace?: boolean; state?: any; params?: Record<string, string> }
type AppNavigateFn = (to: string | number, options?: NavigateOptions) => void

let _navigate: AppNavigateFn | null = null

export const setNavigate = (navigate: AppNavigateFn): void => {
  _navigate = navigate
}

/**
 * Navigate imperatively from outside React components.
 * Falls back to window.location.hash for HashRouter compatibility if React Router
 * is not yet ready.
 */
export const serviceNavigate = (to: string, options?: NavigateOptions): void => {
  if (_navigate) {
    _navigate(to, options)
  } else {
    console.warn('[navigation] serviceNavigate called before React Router was ready, falling back to location')
    // In hash-routing mode (checkmated), createHashRouter uses basename '/!/app', so the hash
    // must be '#/!/app<path>' for React Router to match routes correctly.
    // window.location.search is intentionally NOT touched — query params (tenantUuid, c_selections)
    // live before the '#' and are preserved by the browser when only the hash changes.
    const path = to.startsWith('#') ? to.slice(1) : to
    const hashBasename = '/!/app'
    window.location.hash = `${hashBasename}${path.startsWith('/') ? path : `/${path}`}`
  }
}
